import { match } from '@/utils/pattern-match-util'
import type {
  AllowedProgressionSteps,
  AllowedProgressionTypes,
} from '@/features/inventory/allowed-progressions'
import {
  type InventoryItem,
  type InventoryItemExtensionBillingType,
  InventoryItemExtensionTypeSpecification,
  type InventoryItemPricing,
  type InventoryItemSupportingItemSpec,
  type InventoryItemType,
} from '@/features/inventory/InventoryItem'

/**
 * A service item's price as displayed: one price, or per-step prices for
 * extensions billed by job progression.
 */
export type InventoryItemPriceDisplay =
  | {
      type: 'Single'
      text: string
    }
  | {
      type: 'Progressions'
      progressions: InventoryItemProgressionPriceDisplay[]
    }

export interface InventoryItemProgressionPriceDisplay {
  name: string
  steps: { name: string; priceText: string }[]
}

/**
 * Gets the display name of a pricing type.
 *
 * @param pricing
 */
export function getPricingDisplayName(pricing: InventoryItemPricing) {
  return match(pricing, {
    Fee: 'Fee',
    Flat: 'Flat',
    Markup: 'Markup',
    Markdown: 'Markdown',
  })
}

/**
 * Gets the display name of an inventory item type.
 *
 * @param type
 */
export function getInventoryItemTypeDisplayName(type: InventoryItemType) {
  return match(type, {
    Default: 'Default',
    Bookkeeping: 'Bookkeeping',
    Consultation: 'Consultation',
    Tax: 'Tax',
    Extension: 'Extension',
  })
}

/**
 * Gets the display name of an extension billing type.
 *
 * @param billingType
 */
export function getExtensionBillingTypeDisplayName(
  billingType: InventoryItemExtensionBillingType,
) {
  return match(billingType, {
    FlatFee: 'Flat Fee',
    Percentage: 'Percentage',
  })
}

/**
 * Gets the display name of a supporting item spec.
 *
 * @param spec
 */
export function getSupportingItemSpecDisplayName(
  spec: InventoryItemSupportingItemSpec,
) {
  switch (spec.type) {
    case 'Document':
      return spec.documentType.name
    case 'PersonalInfo':
      return 'Personal Info'
    case 'SpousalInfo':
      return 'Spousal Info'
    case 'DependentsInfo':
      return 'Dependents Info'
    case 'PersonalRefundMethodInfo':
      return 'Personal Refund Method Info'
    case 'BusinessInfo':
      return 'Business Info'
    case 'BusinessRefundMethodInfo':
      return 'Business Refund Method Info'
    case 'Notes':
      return 'Notes'
  }
}

export const ProgressionsNameMapping: Record<AllowedProgressionTypes, string> =
  {
    Outsourcing: 'Outsourcing',
    Essential: 'Essential',
    Review: 'Review',
    Retail: 'Retail',
    Preparation: 'Preparation',
  }

export const ProgressionStepsNameMapping: Record<
  AllowedProgressionSteps,
  string
> = {
  JobStarted: 'Job Started',
  OpenItems: 'Open Items',
  DraftInReview: 'Draft in Review',
  DraftApproved: 'Draft Approved',
  DraftRejected: 'Draft Rejected',
  ReviewCompleted: 'Review Completed',
  ReturnFiled: 'Return Filed',
  AuthorizationSigned: 'Authorization Signed',
  DeliverableCompleted: 'Deliverable Completed',
  PreparationCompleted: 'Preparation Completed',
}

/**
 * Gets the display name of a service item's price type, which for extensions
 * is their billing type.
 *
 * @param inventoryItem
 */
export function getInventoryItemPriceTypeDisplayName(
  inventoryItem: InventoryItem,
) {
  const specification = inventoryItem.typeSpecification
  return specification instanceof InventoryItemExtensionTypeSpecification
    ? getExtensionBillingTypeDisplayName(specification.billingType)
    : getPricingDisplayName(inventoryItem.priceType)
}

/**
 * Gets the client or pro price of a service item for display.
 *
 * @param inventoryItem
 * @param priceFor
 */
export function getInventoryItemPriceDisplay(
  inventoryItem: InventoryItem,
  priceFor: 'Client' | 'Pro',
): InventoryItemPriceDisplay {
  const specification = inventoryItem.typeSpecification
  if (!(specification instanceof InventoryItemExtensionTypeSpecification)) {
    return {
      type: 'Single',
      text: formatPrice(
        priceFor === 'Client' ? inventoryItem.price : inventoryItem.proPrice,
        inventoryItem.priceType,
      ),
    }
  }

  const billingInfo = specification.billingInfo
  if (billingInfo.type === 'Regular') {
    return {
      type: 'Single',
      text: formatExtensionPrice(
        priceFor === 'Client'
          ? billingInfo.clientChargeAmount
          : billingInfo.proPayoutAmount,
        specification.billingType,
      ),
    }
  }

  return {
    type: 'Progressions',
    progressions: billingInfo.progressions.map((progression) => ({
      name: ProgressionsNameMapping[progression.progression],
      steps: progression.steps.map((step) => ({
        name: ProgressionStepsNameMapping[step.step],
        priceText: formatExtensionPrice(
          priceFor === 'Client'
            ? step.clientChargeAmount
            : step.proPayoutAmount,
          specification.billingType,
        ),
      })),
    })),
  }
}

function formatPrice(price: number, priceType: InventoryItemPricing) {
  return priceType === 'Markup' || priceType === 'Markdown'
    ? `${price.toFixed(2)}%`
    : `$${price.toFixed(2)}`
}

function formatExtensionPrice(
  price: number,
  billingType: InventoryItemExtensionBillingType,
) {
  return billingType === 'FlatFee'
    ? `$${price.toFixed(2)}`
    : `${price.toFixed(2)}%`
}
